// Success.tsx
import React, { useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button } from '@mui/material';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CreateIcon from '@mui/icons-material/Create';
import { useSurveyContent } from '../../../../shared/hooks';
import titleCase from '../../../../shared/helpers/titleCase';
import { useAvailabilityData } from '../Availability/hooks';
import { useFinalizeConsultation } from '../ContactInformation/hooks';

type AvailabilityDay = { day: string; date: string; month: string; slots: number };

const Success: React.FC = () => {
  const { surveyContent } = useSurveyContent();
  const handleFinishConsultation = useFinalizeConsultation();
  const cdUserName = surveyContent?.cd_associate_name ?? '';
  const surveyCompensation = surveyContent?.content?.survey_compensation ?? '';
  const contactEmail = surveyContent?.cd_associate_email;
  const mailtoLink = `mailto:${contactEmail}`;

  const [availabilityDays, setAvailabilityDays] = React.useState<AvailabilityDay[]>([]);

  const [showAllDays, setShowAllDays] = React.useState(false);
  const visibleDays = showAllDays ? availabilityDays : availabilityDays.slice(0, 3);
  const redirectToAvailabilities = () => {
    const urlAux = window.location.href.replace('/c/', '/a/');
    window.location.assign(urlAux);
  };

  const getAvailability = useAvailabilityData();

  const weekdayFromISO = (iso: string) => {
    // new Date('YYYY-MM-DD') is specified as UTC midnight. Using getUTCDay is safe.
    const d = new Date(`${iso}T00:00:00Z`);
    const idx = d.getUTCDay(); // 0=Sun .. 6=Sat
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return names[idx];
  };

  const getCurrentAvailability = async (id: string) => {
    const res = await getAvailability(id);
    const fetchedAvailabilities = res?.availabilities ?? [];

    if (!Array.isArray(fetchedAvailabilities) || fetchedAvailabilities.length === 0) {
      setAvailabilityDays([]);
      return;
    }

    // group counts by exact date string (no tz math)
    const byDate: Record<string, number> = {};
    for (const a of fetchedAvailabilities) {
      if (!a?.date) continue;
      byDate[a.date] = (byDate[a.date] ?? 0) + 1;
    }

    // month names without any conversion
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const converted = Object.keys(byDate)
      .sort() // YYYY-MM-DD ascending
      .map((iso) => {
        const [year, mm, dd] = iso.split('-'); // strings
        const monthName = monthNames[parseInt(mm, 10) - 1];
        const wk = weekdayFromISO(iso);
        return {
          day: wk,
          date: dd,
          month: monthName,
          slots: byDate[iso],
        };
      });

    setAvailabilityDays(converted);
  };

  useEffect(() => {
    if (surveyContent?.subject) {
      handleFinishConsultation(surveyContent?.subject);
      getCurrentAvailability(surveyContent?.subject);
    }
  }, [surveyContent?.subject]);

  return (
    <Box
      sx={{
        maxWidth: '480px',
        background: 'white',
        borderRadius: '16px',
        boxShadow:
          ' 0px 5px 12px 4px rgba(0, 0, 0, 0.09), 0px 3px 6px 0px rgba(0, 0, 0, 0.12),  0px 1px 2px -2px rgba(0, 0, 0, 0.16)',
        mx: 'auto',
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box display="flex" justifyContent="center">
        <CheckCircleTwoToneIcon
          sx={{
            height: '50px',
            width: '50px',
            '& path:first-of-type': {
              color: 'rgba(230, 245, 235, 1)', // first tone (usually background tone)
            },
            '& path:last-of-type': {
              color: 'rgba(42, 158, 86, 1)', // second tone (foreground tone)
            },
          }}
        />
      </Box>

      <Typography variant="h3" fontWeight={700} align="center">
        Application Complete!
      </Typography>

      <Typography variant="body2" align="center">
        Thank you for completing the screener. Your application will now be reviewed by our client
        for this consultation opportunity.
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ backgroundColor: '#F0FAFF', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            What’s Next:
          </Typography>

          <Box display="flex" gap={1} mb={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              1
            </Box>
            <Typography variant="body2">
              Your profile will be reviewed by our client within 5 business days
            </Typography>
          </Box>

          <Box display="flex" gap={1} mb={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              2
            </Box>
            <Typography variant="body2">
              If selected, you'll receive a scheduling confirmation email
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <Box
              sx={{
                backgroundColor: 'rgba(222, 233, 252, 1)',
                color: 'rgba(15, 70, 255, 1)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              3
            </Box>
            <Typography variant="body2">
              Payment (${surveyCompensation}) will be processed after the consultation
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ border: 'none' }}>
        <CardContent sx={{ borderRadius: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              Your Submitted Availability:
            </Typography>
            <Typography
              onClick={redirectToAvailabilities}
              variant="body2"
              sx={{
                color: 'rgba(15, 75, 255, 1)',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <CreateIcon sx={{ height: 20, width: 20 }} />
              <span>Update</span>
            </Typography>
          </Box>

          {/* Day items */}
          {visibleDays.map((d, i) => (
            <Box
              key={`${d.day}-${d.date}-${i}`}
              sx={{
                backgroundColor: '#F0FAFF',
                borderRadius: '12px',
                padding: '16px 16px',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box textAlign="center" mr={1}>
                  <Typography
                    variant="h2"
                    fontWeight={700}
                    sx={{ lineHeight: 1, color: 'rgba(37, 99, 235, 1)' }}
                  >
                    {d.date}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.month}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {d.day}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.slots} time slots selected
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}

          {/* View All / Show Less link */}
          {availabilityDays.length > 3 && (
            <Typography
              role="button"
              tabIndex={0}
              onClick={() => setShowAllDays((s) => !s)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowAllDays((s) => !s)}
              variant="body2"
              align="center"
              sx={{
                color: 'rgba(15, 75, 255, 1)',
                fontWeight: 600,
                mt: 1,
                cursor: 'pointer',
                userSelect: 'none',
                outline: 'none',
              }}
            >
              {showAllDays ? 'Show Less' : `View All ${availabilityDays.length} Days`}
            </Typography>
          )}
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Have Questions?
          </Typography>
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <AccountCircleIcon sx={{ height: '40px', width: '40px' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={500}>
                {cdUserName && cdUserName.length > 1 ? titleCase(cdUserName) : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contactEmail}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            component="a"
            href={mailtoLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            CONTACT
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Success;
